// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useEffect, useRef, useState } from 'react';

/**
 * Counts down `estimateSeconds` while `active`; returns the remaining seconds, which
 * stay at 0 ("still working") once the resync outlasts the estimate and reset when
 * `active` clears. The estimate is latched when the countdown starts: a mid-resync
 * refetch changing `estimateSeconds` (e.g. on window refocus, while the resync itself
 * mutates shipment state) must not snap the countdown back to full.
 */
export function useResyncCountdown(
  active: boolean,
  estimateSeconds: number
): number {
  const [remainingSeconds, setRemainingSeconds] = useState(estimateSeconds);
  const latchedSecondsRef = useRef(estimateSeconds);

  // While idle, track the live estimate so the next countdown starts from it.
  useEffect(() => {
    if (!active) {
      latchedSecondsRef.current = estimateSeconds;
      setRemainingSeconds(estimateSeconds);
    }
  }, [active, estimateSeconds]);

  useEffect(() => {
    if (!active || latchedSecondsRef.current <= 0) {
      return;
    }
    setRemainingSeconds(latchedSecondsRef.current);
    const intervalId = setInterval(() => {
      setRemainingSeconds((previous) => Math.max(previous - 1, 0));
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [active]);

  return remainingSeconds;
}
